import { generateSong, sectionAt, songSeconds, stepSeconds, type Song } from './songs';
import { stationDef, stationSeed, type StationId } from './stations';
import { talkItem, type TalkItem, type TalkKind } from './talk';

/**
 * A station's running order: an endless, deterministic sequence of items (songs, DJ links, adverts,
 * jingles, news...). Each station keeps its own clock from the moment it is first tuned, so tuning
 * away and back lands mid-record, as a real station would. A DJ link ahead of a record talks over its
 * intro: the record starts `overlap` seconds before the link ends and is ducked until the DJ stops.
 */

export type ItemKind = TalkKind | 'song';

export interface Item {
  kind: ItemKind;
  /** Position in the station's running order. */
  index: number;
  /** Station time (s) the item starts. */
  start: number;
  duration: number;
  /** Seconds the NEXT item starts before this one ends (a link talking over a record's intro). */
  overlap: number;
  song: Song | null;
  talk: TalkItem | null;
  title: string;
}

const COMMERCIAL_CYCLE: readonly ItemKind[] = ['jingle', 'link', 'song', 'link', 'song', 'advert', 'advert', 'jingle', 'timecheck', 'song', 'link', 'song', 'song'];
const PUBLIC_CYCLE: readonly ItemKind[] = ['jingle', 'link', 'song', 'link', 'song', 'timecheck', 'song', 'jingle', 'link', 'song', 'link', 'song'];
const TALK_CYCLE: readonly ItemKind[] = ['pips', 'news', 'weather', 'jingle', 'phoneIn', 'advert', 'traffic', 'jingle', 'phoneIn', 'advert', 'sport', 'phoneIn', 'jingle', 'news', 'advert', 'phoneIn'];

export function cycleFor(station: StationId): readonly ItemKind[] {
  const def = stationDef(station);
  if (!def.style) return TALK_CYCLE;
  return def.adverts ? COMMERCIAL_CYCLE : PUBLIC_CYCLE;
}

/** Kind of item `i` and its running number among items of that kind. */
export function slotOf(station: StationId, i: number): { kind: ItemKind; n: number } {
  const cycle = cycleFor(station);
  const c = Math.floor(i / cycle.length);
  const slot = i % cycle.length;
  const kind = cycle[slot] as ItemKind;
  let before = 0;
  for (let k = 0; k < slot; k++) if (cycle[k] === kind) before++;
  const perCycle = cycle.filter((k) => k === kind).length;
  return { kind, n: c * perCycle + before };
}

/** Seconds of a song's intro section (what a DJ may talk over). */
export function introSeconds(song: Song): number {
  const intro = sectionAt(song, 0);
  return intro.kind === 'intro' ? intro.bars * 16 * stepSeconds(song.bpm) : 0;
}

export class Programme {
  private readonly items: Item[] = [];

  constructor(readonly station: StationId) {}

  /** Item `i` (built and cached on demand). */
  item(i: number): Item {
    while (this.items.length <= i) this.build(this.items.length);
    return this.items[i] as Item;
  }

  private build(i: number): void {
    const prev = i > 0 ? (this.items[i - 1] as Item) : null;
    const { kind, n } = slotOf(this.station, i);
    const start = prev ? prev.start + prev.duration - prev.overlap : 0;
    let item: Item;
    if (kind === 'song') {
      const song = generateSong(this.station, n);
      item = { kind, index: i, start, duration: songSeconds(song), overlap: 0, song, talk: null, title: `${song.artist}: ${song.title}` };
    } else {
      const nextKind = slotOf(this.station, i + 1).kind;
      const talkOver = kind === 'link' && nextKind === 'song';
      const talk = talkItem(kind, this.station, n, talkOver);
      let overlap = 0;
      if (talkOver) {
        const nextSong = generateSong(this.station, slotOf(this.station, i + 1).n);
        // Start the record so the DJ finishes about half a second before the intro ends.
        overlap = Math.max(0, Math.min(talk.duration - 0.6, introSeconds(nextSong) - 0.5));
      }
      item = { kind, index: i, start, duration: talk.duration, overlap, song: null, talk, title: talk.title };
    }
    this.items.push(item);
  }

  /**
   * Station time to start the clock at when the station is first tuned: 20-60 % of the way into its
   * first record (the first long item on the talk station: the news), so tuning in for the first time
   * lands on something already under way instead of every station opening on its ident and a link.
   */
  tuneInOffset(): number {
    let target: Item | null = null;
    for (let i = 0; i < 6 && !target; i++) {
      const it = this.item(i);
      if (it.kind === 'song' || it.duration > 12) target = it;
    }
    target ??= this.item(1);
    const frac = 0.2 + 0.4 * (stationSeed(this.station, 31, 0) / 4294967296);
    return target.start + frac * target.duration;
  }

  /** Items overlapping the station-time window [from, to). */
  itemsIn(from: number, to: number): Item[] {
    const out: Item[] = [];
    for (let i = 0; ; i++) {
      const it = this.item(i);
      if (it.start >= to) break;
      if (it.start + it.duration > from) out.push(it);
    }
    return out;
  }

  /** The item "on air" at station time `t`: the song if one is playing under a link, else the talk. */
  onAir(t: number): Item {
    const live = this.itemsIn(t, t + 1e-6);
    const song = live.find((it) => it.kind === 'song');
    return song ?? live[live.length - 1] ?? this.item(0);
  }

  /** Drop cached items that ended before station time `t` (memory stays bounded on long drives). */
  forget(t: number): void {
    // Items are indexed by position: keep the array but release the heavy song objects.
    for (const it of this.items) if (it.song && it.start + it.duration < t - 60) it.song = null;
  }

  /** Re-materialise a song dropped by `forget` (the item's slot fully determines it). */
  songOf(it: Item): Song {
    if (!it.song) it.song = generateSong(this.station, slotOf(this.station, it.index).n);
    return it.song;
  }
}
